import { SHIPPO_LABEL_URL } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { generateFullAddress } from '@/shared/lib';
import axios from 'axios';
import _ from 'lodash';
import { useMemo } from 'react';
import type { TicketInboundProps } from './ticketInbound.types';

/**
 * Prepares the inbound RMA ticket view: the per-device return rows, notification e-mails, clinic notes, shipping and returning address rows and the shipment labels. Memoized on the ticket data, it derives the label number the device shipped under and its Delivered or Pending status, builds the sender and recipient address, zip, phone and e-mail comparison rows, and kicks off the label PDF download.
 *
 * @param data - The RMA ticket whose inbound shipment is displayed.
 * @returns The downloaded label PDFs plus the device rows, notification e-mails, clinic notes, shipping info rows and labels.
 */
const useTicketInbound = ({ data }: Pick<TicketInboundProps, 'data'>) => {
  const [state, setState] = useMergeState({
    filePDF: [],
  });

  /**
   * Downloads the Shippo shipping-label PDFs of the inbound RMA labels so the ticket view can preview them. Rewrites each label URL onto the SHIPPO_LABEL_URL proxy with the original host passed as a target header, requests every label as an arraybuffer, and stores the collected buffers as filePDF, logging any failure.
   *
   * @param file - Shipment labels whose transaction carries the label URL to download.
   * @returns Resolves once the label requests have been dispatched.
   */
  const formatFilePDF = async (file: any): Promise<any> => {
    const promise: any = [];
    _.map(file, ({ transaction }) => {
      const splitString = _.split(transaction.labelUrl, '.com/');
      const newURL = `${SHIPPO_LABEL_URL}${splitString[1]}`;
      const response = axios.get(newURL, {
        responseType: 'arraybuffer',
        headers: {
          target: `${splitString[0]}.com/`,
        },
      });
      promise.push(response);
    });
    Promise.all(promise)
      .then((data): any => {
        const filePDF: any = [];
        _.forEach(data, (item) => {
          filePDF.push(item.data);
        });
        setState({ filePDF });
      })
      .catch((err) => console.error('Error Format PDF', err));
  };

  /**
   * Memoized view model for the RMA inbound tab: the device rows, notification emails, clinic notes, labels and the shipping-versus-returning address comparison. Records the 1-based index of the label containing each device, derives the shipping status from isCompleted, and triggers the label PDF download as a side effect.
   *
   * @returns The inbound tab view model, or an empty object when no ticket was supplied.
   */
  const formatDataInbound = useMemo((): any => {
    if (data) {
      const rmaDevicesArray: any = [];
      const { senderInformation, recipientInformation, notificationEmail, clinicNotes } = data;
      const { contact: senderContact } = senderInformation;
      const { devices: inboundDevices, labels, contact: recipientContact } = recipientInformation;
      formatFilePDF(labels);
      _.forEach(inboundDevices, (device) => {
        const deviceId = device.device?.deviceId;
        let labelIndex;
        _.forEach(labels, (label, index) => {
          /**
           * The device id from a label's shipment devices that matches the inbound device being processed. Obtained by mapping the label's shipmentDevices to their deviceId values and searching for an exact match.
           *
           * @returns The matching device id, or undefined when the label does not contain it.
           */
          const foundDevice = _.find(
            label.shipmentDevices.map((device: any) => device.deviceId),
            (id) => deviceId === id,
          );
          if (foundDevice) {
            labelIndex = index + 1;
          }
        });
        rmaDevicesArray.push({
          deviceId,
          labelIndex,
          model: device.device?.model,
          replacedDevice: device.replacedDevice,
          purpose: device.purpose,
          shippingStatus: device.isCompleted ? 'Delivered' : 'Pending',
          isRenewal: device.isRenewal,
        });
      });
      const shippingInfo = [
        {
          title: 'Address:',
          shippingContent: generateFullAddress({
            contact: {
              ...senderContact,
              address: senderContact.street1,
            },
            includedCountry: true,
          }),
          returningContent: generateFullAddress({
            contact: {
              ...recipientContact,
              address: recipientContact.street1,
            },
            includedCountry: true,
          }),
        },
        {
          title: 'Zip code:',
          shippingContent: senderContact.zip,
          returningContent: recipientContact.zip,
        },
        {
          title: 'Contact phone number:',
          shippingContent: senderContact.phone,
          returningContent: recipientContact.phone,
        },
        {
          title: 'Contact email:',
          shippingContent: senderContact.email,
          returningContent: recipientContact.email,
        },
      ];
      return {
        rmaDevicesArray,
        notificationEmail: notificationEmail?.split(','),
        clinicNotes,
        shippingInfo,
        labels,
      };
    }
    return {};
  }, [data]);

  const { rmaDevicesArray, notificationEmail, clinicNotes, shippingInfo, labels } =
    formatDataInbound;

  return {
    state,
    rmaDevicesArray,
    notificationEmail,
    clinicNotes,
    shippingInfo,
    labels,
  };
};

export { useTicketInbound };
